import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectorRef, Component, input, inject, DestroyRef, effect, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { catchError, finalize, map, merge, Observable, of, Subscription, switchMap, tap } from 'rxjs';

import { ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls/svc-chip';
import { ISvcSelectOption } from '@solvace/component/svc-controls';
import { HttpErrorService } from '@solvace/common/services/http-error.service';
import { SvcCustomField, SvcCustomFieldList, SvcFormCustomFieldValue, SvcFormCustomFieldValueData, SvcFormCustomFieldValueFields } from './svc-form-custom-field.interface';
import { SvcFormCustomFieldErrorCode, SvcFormCustomFieldType } from './svc-form-custom-field.enum';
import { SvcFormCustomFieldValueService } from './svc-form-custom-field-value.service';
import { SvcFormCustomFieldHttpClient } from './svc-form-custom-field-http-client';
import { SvcFormCustomFieldOptionsService } from './svc-form-custom-field-options.service';
import { UserService } from '@solvace/core/features/user/services/user.service';

@Component({
  selector: 'svc-form-custom-field',
  templateUrl: './svc-form-custom-field.component.html',
  styleUrls: ['./svc-form-custom-field.component.scss'],
  providers: [SvcFormCustomFieldHttpClient, SvcFormCustomFieldValueService, SvcFormCustomFieldOptionsService],
})
export class SvcFormCustomFieldComponent {
  /**
   * @deprecated prop will removed in features, because we became featureId a guid by module
   * Name of the feature, such as "bos-type" or "rca-type",
   * which corresponds to the type displayed in the registration form.
   */
  public featureName = input('');

  /**
   * Guid to use by module called custom fields
   */
  public featureId = input('');

  /**
   * Unique identifier for the template (templateId) created in the custom fields registration screen
   */
  public registryId = input<number>();

  /**
   * External reference ID for the register in the module,
   * such as the ID of a BOS, alert, etc.
   */
  public systemReferenceId = input<number>();

  /**
   * If the display don't used templateId (registryId)
   */
  public onlyCustomFields = input(false);

  public isViewMode = input(false);
  public applicationId = input<string>(null);

  /**
   * Stacks every field one below the other at all widths, instead of two columns from `sm` up.
   */
  public singleColumn = input(false);

  readonly #errorService = inject(HttpErrorService);
  readonly #fb = inject(FormBuilder);
  readonly #customFieldValueService = inject(SvcFormCustomFieldValueService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #userService = inject(UserService);
  readonly #optionsService = inject(SvcFormCustomFieldOptionsService);
  readonly #httpClient = inject(SvcFormCustomFieldHttpClient);
  // Default-CD component that may live under an OnPush host (e.g. the CIL execution custom-fields
  // modal). Its async loads mutate plain fields, so it must mark itself — and its OnPush ancestors —
  // for check when they resolve, otherwise the view stays on its loading state until an unrelated
  // click ticks change detection (AB#69059).
  readonly #cdr = inject(ChangeDetectorRef);

  #loadedCustomFields = false;
  #loadedUsersIds = false;
  #loadedTeams = false;
  #loadedHashtags = false;
  #equipmentLocationKey: string | null = null;
  #locationChanges?: Subscription;
  #recordSiteId: number | undefined;

  public isLoading = false;
  protected customFields = signal<SvcCustomFieldList>(null);
  protected fieldTypes = SvcFormCustomFieldType;
  protected isLoadingUsersIds = false;
  protected usersIds: string[] = [];
  protected teamOptions: ISvcChipOption[] = [];
  protected hashtagOptions: ISvcChipGroupedOption[] = [];
  protected equipmentOptions: ISvcSelectOption[] = [];
  protected isLoadingTeams = false;
  protected isLoadingHashtags = false;
  protected isLoadingEquipments = false;
  protected fieldTypesMaxLength = {
    Text: 300,
    LongText: 4000,
    Number: 20,
  };

  public formCustomFields: FormGroup = this.#fb.group({
    fields: this.#fb.array([]),
  });

  constructor() {
    this.#onChanges();
  }

  #onChanges(): void {
    effect(() => {
      if (this.registryId() || this.featureName() || this.featureId() || this.onlyCustomFields()) this.#getCustomFields();
    });

    effect(() => {
      if (this.isViewMode()) {
        this.formCustomFields.disable({ emitEvent: false });
      } else {
        this.formCustomFields.enable({ emitEvent: false });
      }
    });
  }

  #isArrayStringType(type: number): boolean {
    return [SvcFormCustomFieldType.List, SvcFormCustomFieldType.ListMultiple, SvcFormCustomFieldType.User, SvcFormCustomFieldType.Team, SvcFormCustomFieldType.Hashtag]?.includes(
      type
    );
  }

  #isArrayNumberType(type: number): boolean {
    return [SvcFormCustomFieldType.Location, SvcFormCustomFieldType.Area]?.includes(type);
  }

  #normalizeFieldTypeId(value: unknown): number {
    const normalizedFieldTypeId = Number(value);
    return Number.isFinite(normalizedFieldTypeId) ? normalizedFieldTypeId : 0;
  }

  #getFieldValueByType(field: SvcCustomField): any {
    const value = field?.fieldValue?.[0];
    const fieldTypeId = this.#normalizeFieldTypeId(field?.fieldTypeId);
    const valueIsEmpty = value === '' || value === null || value === undefined;

    if (fieldTypeId === this.fieldTypes.Checkbox) {
      if (valueIsEmpty || value === null || value === undefined) return false;
      if (value === true || value === 'true' || value === 1 || value === '1') return true;
      if (value === false || value === 'false' || value === 0 || value === '0') return false;
      return Boolean(value);
    } else if (field?.fieldTypeId === this.fieldTypes.Text) return ((value ?? '') as string).substring(0, this.fieldTypesMaxLength.Text);
    else if (fieldTypeId === this.fieldTypes.LongText) return ((value ?? '') as string).substring(0, this.fieldTypesMaxLength.LongText);
    else if (fieldTypeId === this.fieldTypes.Number) return valueIsEmpty ? '' : Number?.(value.substring(0, this.fieldTypesMaxLength.Number));
    else if (this.#isArrayNumberType(fieldTypeId)) return valueIsEmpty ? [] : [Number?.(value.substring(0, this.fieldTypesMaxLength.Number))];
    // AB#75193 an unanswered list is persisted as [''], which Validators.required accepted as a value
    else if (this.#isArrayStringType(fieldTypeId)) return Array.isArray(field?.fieldValue) ? field.fieldValue.filter((item) => item !== '' && item !== null && item !== undefined) : field?.fieldValue;
    else if (fieldTypeId === this.fieldTypes.Equipment) return value ?? '';
    return value;
  }

  #setValueForm(): void {
    this.customFields()?.fields?.forEach((field: SvcCustomField, index: number) => {
      const fields = this.formCustomFields?.get?.('fields') as FormArray;
      const fieldControl = (fields?.controls?.[index] as FormGroup)?.controls?.fieldValue;
      fieldControl.setValue(field?.fieldValue);
    });
    if (this.isViewMode()) {
      this.formCustomFields.disable({ emitEvent: false });
    }
  }

  #normalizeRegistryId(value: unknown): number | null | undefined {
    if (value === undefined || value === '') return undefined;
    if (value === null) return null;

    const normalizedRegistryId = Number(value);
    return Number.isFinite(normalizedRegistryId) ? normalizedRegistryId : undefined;
  }

  public setCustomFields(customFields: SvcFormCustomFieldValueData, registryId: number | null): void {
    const currentCustomFields = this.customFields();
    const resolvedFeatureName = this.featureName() || customFields?.featureName || currentCustomFields?.featureName;
    const resolvedFeatureId = this.featureId() || customFields?.featureId || currentCustomFields?.featureId;
    const hasRegistryIdFromGet = Object.prototype.hasOwnProperty.call(customFields ?? {}, 'registryId');
    const resolvedRegistryId = hasRegistryIdFromGet
      ? this.#normalizeRegistryId(customFields?.registryId)
      : this.#normalizeRegistryId(registryId ?? this.registryId() ?? currentCustomFields?.registryId);

    this.customFields.set({
      featureName: resolvedFeatureName,
      featureId: resolvedFeatureId,
      registryId: resolvedRegistryId,
      fields: customFields?.fields?.map((field) => {
        const fieldTypeId = this.#normalizeFieldTypeId(field?.fieldTypeId);
        return {
          ...field,
          fieldTypeId,
          fieldValue: this.#getFieldValueByType(field),
          optionsCombo: this.#getOptionsCombo(field?.options),
        };
      }),
    });
    this.#createFormCustomFields();
    this.#setValueForm();
    this.#bindEquipmentToLocations();
  }

  /**
   * @param registryId Template ID. If the module does not use a template, you can pass null.
   * @param systemReferenceId ID of the record (e.g., BOS, alert, etc.)
   * @param siteId Site the record belongs to. Defaults to the logged user's current site.
   */
  public getCustomFieldByExternalReferenceId(registryId: number | null = null, systemReferenceId: number, siteId: number = undefined): void {
    this.isLoading = true;
    this.#recordSiteId = siteId;
    this.#customFieldValueService
      .getCustomFieldValue(this.featureName(), registryId, systemReferenceId, this.featureId(), this.applicationId(), siteId)
      .pipe(
        tap((customFields: SvcFormCustomFieldValueData) => {
          this.setCustomFields(customFields, registryId);
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return err;
        }),
        finalize(() => {
          this.isLoading = false;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #canGetCustomFields(): boolean {
    const hasDataToGetCustomFields = !this.systemReferenceId() && !this.#loadedCustomFields && !this.isLoading;

    if (this.onlyCustomFields()) return hasDataToGetCustomFields && !!this.featureId();
    return hasDataToGetCustomFields && (!!this.featureName() || !!this.featureId()) && !!this.registryId();
  }

  #getCustomFields(): void {
    if (this.#canGetCustomFields()) {
      this.isLoading = true;
      this.#customFieldValueService
        .getCustomField(this.featureName(), this.registryId(), this.featureId(), this.applicationId())
        .pipe(
          tap((customFields: SvcCustomFieldList) => {
            const currentCustomFields = this.customFields();
            this.customFields.set({
              ...customFields,
              fields: customFields?.fields?.map((field) => ({
                ...field,
                fieldTypeId: this.#normalizeFieldTypeId(field?.fieldTypeId),
              })),
              featureName: this.featureName() || customFields?.featureName || currentCustomFields?.featureName,
              featureId: this.featureId() || customFields?.featureId || currentCustomFields?.featureId,
              registryId: Object.prototype.hasOwnProperty.call(customFields ?? {}, 'registryId')
                ? this.#normalizeRegistryId(customFields?.registryId)
                : this.#normalizeRegistryId(this.registryId() ?? currentCustomFields?.registryId),
            });
            this.#createFormCustomFields();
            this.#bindEquipmentToLocations();
          }),
          catchError((err) => {
            this.#errorService.showErrorInToast(err);
            return err;
          }),
          finalize(() => {
            this.isLoading = false;
            this.#loadedCustomFields = true;
            this.#cdr.markForCheck();
          }),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe();
    }
  }

  #getOptionsCombo(options: string[]): ISvcChipOption[] {
    return options?.map((option) => ({
      label: option,
      value: option,
    }));
  }

  #setValidators(customField: SvcCustomField, field: FormGroup): void {
    const validators = [];
    if (customField?.mandatory) {
      const isCheckbox = customField?.fieldTypeId === SvcFormCustomFieldType.Checkbox;
      validators.push(isCheckbox ? Validators.requiredTrue : Validators.required);
    }

    if (customField?.fieldTypeId === SvcFormCustomFieldType.Hour) validators.push(this.#hourValidator);

    if (customField?.fieldTypeId === SvcFormCustomFieldType.Number) validators.push(Validators.maxLength(this.fieldTypesMaxLength.Number));

    if (customField?.fieldTypeId === SvcFormCustomFieldType.Text) validators.push(Validators.maxLength(this.fieldTypesMaxLength.Text));

    if (customField?.fieldTypeId === SvcFormCustomFieldType.LongText) validators.push(Validators.maxLength(this.fieldTypesMaxLength.LongText));

    field.get('fieldValue').setValidators(validators);
  }

  #createFormCustomFields(): void {
    this.formCustomFields?.reset();
    const fields = this.formCustomFields?.get?.('fields') as FormArray;
    fields?.clear();

    this.customFields()?.fields?.forEach((customField: SvcCustomField) => {
      customField.optionsCombo = this.#getOptionsCombo(customField?.options);

      const field: FormGroup = this.#fb.group({
        customFieldListId: [customField.customFieldListId],
        fieldValue: [],
        fieldTypeId: [customField.fieldTypeId],
      });

      if (customField?.fieldTypeId === SvcFormCustomFieldType.User) this.#getUsersIds();
      if (customField?.fieldTypeId === SvcFormCustomFieldType.Team) this.#getTeamOptions();
      if (customField?.fieldTypeId === SvcFormCustomFieldType.Hashtag) this.#getHashtagOptions();

      this.#setValidators(customField, field);

      fields.push(field);
    });
  }

  #hourValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value;

    if (!value) return null;

    const isValid = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/.test(value);

    return isValid ? null : { invalid24Hour: true };
  }

  #getValueCustomField(field: any): SvcFormCustomFieldValueFields {
    return {
      customFieldListId: field?.customFieldListId,
      fieldValue: [String(field?.fieldValue ?? '')],
    };
  }

  #getFieldsValue(): SvcFormCustomFieldValueFields[] {
    const fields: SvcFormCustomFieldValueFields[] = [];

    this.formCustomFields?.value?.fields?.forEach((field) => {
      if (field?.fieldTypeId === SvcFormCustomFieldType.Checkbox) {
        const checkboxValue = field?.fieldValue === true || field?.fieldValue === 'true' || field?.fieldValue === 1 || field?.fieldValue === '1';
        fields.push({
          ...this.#getValueCustomField(field),
          fieldValue: [String(checkboxValue)],
        });
      } else if (this.#isArrayStringType(field?.fieldTypeId))
        fields.push({
          ...this.#getValueCustomField(field),
          fieldValue: field?.fieldValue?.length ? field?.fieldValue : [''],
        });
      else fields.push(this.#getValueCustomField(field));
    });

    return fields;
  }

  #getDataToAddCustomFieldValue(externalReferenceId: number, fields: SvcFormCustomFieldValueFields[]): SvcFormCustomFieldValue {
    const featureName = this.#resolveFeatureName();
    const registryId = this.#resolveRegistryId();
    const featureId = this.#resolveFeatureId();

    return {
      ...(featureName ? { featureName } : {}),
      ...(registryId !== undefined ? { registryId } : {}),
      systemReferenceId: this.systemReferenceId() ?? externalReferenceId,
      fields,
      ...(featureId ? { featureId } : {}),
    };
  }

  public changeCustomFieldRecord(externalReferenceId: number): Observable<boolean> {
    const fields: SvcFormCustomFieldValueFields[] = this.#getFieldsValue();

    if (fields?.length) {
      const dataCustomFields = this.#getDataToAddCustomFieldValue(externalReferenceId, fields);
      const registryIdForGet = this.#normalizeRegistryId(this.registryId());
      const systemRef = this.systemReferenceId() ?? externalReferenceId;

      return this.#customFieldValueService.updateCustomFieldValue(dataCustomFields, this.applicationId(), this.#recordSiteId).pipe(
        switchMap(() =>
          this.#customFieldValueService.getCustomFieldValue(this.featureName(), registryIdForGet, systemRef, this.featureId(), this.applicationId(), this.#recordSiteId)
        ),
        tap((customFields: SvcFormCustomFieldValueData) => {
          this.setCustomFields(customFields, registryIdForGet ?? null);
        }),
        map(() => true)
      );
    }

    return of(true);
  }

  public formValid(): boolean {
    this.formCustomFields?.markAllAsTouched();
    return this.formCustomFields?.valid;
  }

  public formatHour(control: AbstractControl): void {
    const value = control?.value;
    if (!value) return;

    const formattedValue = value.replace(/^(\d{1,2}):?(\d{0,2})$/, (_match, hours, minutes) => {
      hours = hours.padStart(2, '0');
      minutes = minutes.padEnd(2, '0');

      hours = Math.min(23, parseInt(hours)).toString().padStart(2, '0');
      minutes = Math.min(59, parseInt(minutes)).toString().padStart(2, '0');

      return `${hours}:${minutes}`;
    });

    if (value !== formattedValue) control.setValue(formattedValue, { emitEvent: false });
  }

  #getUsersIds(): void {
    if (this.#loadedUsersIds && this.isLoadingUsersIds) return;

    this.isLoadingUsersIds = true;
    this.#userService
      .getUsers(true)
      .pipe(
        map((users) => users?.map?.((user) => user?.userId)),
        tap((ids) => (this.usersIds = ids)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return err;
        }),
        finalize(() => {
          this.isLoadingUsersIds = false;
          this.#loadedUsersIds = true;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #getTeamOptions(): void {
    if (this.#loadedTeams || this.isLoadingTeams) return;

    this.isLoadingTeams = true;
    this.#optionsService
      .getTeamOptions()
      .pipe(
        tap((options) => (this.teamOptions = options)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of([]);
        }),
        finalize(() => {
          this.isLoadingTeams = false;
          this.#loadedTeams = true;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #getHashtagOptions(): void {
    if (this.#loadedHashtags || this.isLoadingHashtags) return;

    this.isLoadingHashtags = true;
    this.#optionsService
      .getHashtagOptions(this.#recordSiteId ?? this.#httpClient.siteId)
      .pipe(
        tap((options) => (this.hashtagOptions = options)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of([]);
        }),
        finalize(() => {
          this.isLoadingHashtags = false;
          this.#loadedHashtags = true;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #fieldValueControlsOfType(type: SvcFormCustomFieldType): AbstractControl[] {
    const fields = this.formCustomFields?.get?.('fields') as FormArray;
    return (fields?.controls ?? []).filter((group) => group.get('fieldTypeId')?.value === type).map((group) => group.get('fieldValue'));
  }

  #selectedLocationIds(): number[] {
    return this.#fieldValueControlsOfType(SvcFormCustomFieldType.Location)
      .flatMap((control) => (Array.isArray(control.value) ? control.value : [control.value]))
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  #bindEquipmentToLocations(): void {
    this.#locationChanges?.unsubscribe();
    this.#equipmentLocationKey = null;
    if (!this.#fieldValueControlsOfType(SvcFormCustomFieldType.Equipment).length) return;

    this.#refreshEquipmentOptions(false);
    const locationControls = this.#fieldValueControlsOfType(SvcFormCustomFieldType.Location);
    if (!locationControls.length) return;

    this.#locationChanges = merge(...locationControls.map((control) => control.valueChanges))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#refreshEquipmentOptions(true));
  }

  #refreshEquipmentOptions(clearSelection: boolean): void {
    const locationIds = this.#selectedLocationIds();
    const locationKey = [...locationIds].sort((a, b) => a - b).join(',');
    if (locationKey === this.#equipmentLocationKey) return;

    this.#equipmentLocationKey = locationKey;
    if (clearSelection) this.#fieldValueControlsOfType(SvcFormCustomFieldType.Equipment).forEach((control) => control.setValue('', { emitEvent: false }));

    this.isLoadingEquipments = true;
    this.#optionsService
      .getEquipmentOptions(locationIds)
      .pipe(
        tap((options) => (this.equipmentOptions = options)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of([]);
        }),
        finalize(() => {
          this.isLoadingEquipments = false;
          this.#cdr.markForCheck();
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public hasFields(): boolean {
    return this.#getFieldsValue()?.length > 0;
  }

  public applyMandatoryFieldErrors(error: HttpErrorResponse): boolean {
    const missingFieldIds = this.#mandatoryFieldIds(error);
    if (!missingFieldIds.length) return false;

    const fields = this.formCustomFields?.get('fields') as FormArray;
    fields?.controls?.forEach((field) => {
      if (!missingFieldIds.includes(Number(field.get('customFieldListId')?.value))) return;
      const fieldValue = field.get('fieldValue');
      fieldValue?.setErrors({ ...fieldValue.errors, required: true });
      fieldValue?.markAsTouched();
    });
    this.#cdr.markForCheck();

    return true;
  }

  #mandatoryFieldIds(error: HttpErrorResponse): number[] {
    if (error?.status !== HttpStatusCode.BadRequest) return [];

    const errors: unknown = error.error?.errors;
    if (!errors || typeof errors !== 'object') return [];

    return Object.entries(errors)
      .filter(([, codes]) => Array.isArray(codes) && codes.includes(SvcFormCustomFieldErrorCode.MandatoryField))
      .map(([fieldId]) => Number(fieldId))
      .filter(Number.isInteger);
  }

  #resolveFeatureName(): string | undefined {
    const featureName = this.featureName() || this.customFields()?.featureName;
    if (typeof featureName !== 'string') return undefined;

    const normalizedFeatureName = featureName.trim();
    return normalizedFeatureName.length ? normalizedFeatureName : undefined;
  }

  #resolveRegistryId(): number | null | undefined {
    const customFieldsRegistryId = this.#normalizeRegistryId(this.customFields()?.registryId);
    if (customFieldsRegistryId !== undefined) {
      return customFieldsRegistryId;
    }

    return this.#normalizeRegistryId(this.registryId());
  }

  #resolveFeatureId(): string | undefined {
    const featureId = this.featureId() || this.customFields()?.featureId;
    if (typeof featureId !== 'string') return undefined;

    const normalizedFeatureId = featureId.trim();
    return normalizedFeatureId.length ? normalizedFeatureId : undefined;
  }

  public getFieldsData(): SvcFormCustomFieldValue {
    const fields: SvcFormCustomFieldValueFields[] = this.#getFieldsValue();
    const featureName = this.#resolveFeatureName();
    const registryId = this.#resolveRegistryId();
    const featureId = this.#resolveFeatureId();

    if (fields?.length) {
      return {
        ...(featureName ? { featureName } : {}),
        ...(registryId !== undefined ? { registryId } : {}),
        fields,
        ...(featureId ? { featureId } : {}),
      };
    }

    return null;
  }
}
